import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Loading, NavBtn } from 'components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	useAPI,
	useBizLogicActions,
	useErrorActions,
	useSDK,
	useWXSDK,
} from 'stores';
import { MotionWrapper } from 'utils';

interface ScanProps {
	gift?: boolean;
}

const Scan = ({ gift = false }: ScanProps) => {
	const [verifying, setVerifying] = useState(false);
	const { t } = useTranslation();
	const { sdk, authMode, torchAvailability } = useSDK();
	const { authenticateUAID } = useAPI();
	const wx = useWXSDK();
	let { showError, handleWebscanError, handleSDKScanError } =
		useErrorActions();
	const { handleQueryResponse, setGiftMode } = useBizLogicActions();

	setGiftMode(gift);

	useEffect(() => {
		if (!sdk || typeof authMode === 'undefined') {
			showError(42100, 'SDK is not loaded!', {});
			return;
		}
		if (process.env['REACT_APP_ENABLE_MODE' + authMode] === 'false') {
			handleWebscanError(42100);
			return;
		}

		let verify;
		if (authMode === 1) {
			if (gift || process.env.REACT_APP_QR_ONLY === 'true') {
				verify = async () => {
					return sdk!.manualQRReaderHandler();
				};
			} else {
				verify = async () => {
					return sdk!.manualPhotoModeHandler();
				};
			}
		} else if (authMode === 9) {
			console.log('wx scan');
			verify = () => {
				return new Promise((resolve, reject) => {
					setVerifying(true);
					wx.scanQRCode({
						needResult: 1,
						scanType: ['qrCode'],
						onlyFromCamera: true,
						success: (res: any) => {
							console.log('scan is completed');
							authenticateUAID(
								res.resultStr,
								null,
								null,
								null,
								null,
								false
							).then((res) => {
								resolve({
									code: 0,
									data: res,
								});
							});
						},
						fail: () => {
							console.log('scan is failed');
						},
						cancel: () => {
							console.log('scan is cancelled');
							wx.closeWindow();
						},
					});
				});
			};
		} else {
			if (gift || process.env.REACT_APP_QR_ONLY === 'true') {
				verify = async () => {
					return sdk!.QRReaderHandler(
						document.getElementById('parentElement'),
						document.getElementById('torch')
					);
				};
			} else {
				verify = async () => {
					return sdk!.guidedModeHandler(
						document.getElementById('parentElement'),
						document.getElementById('torch')
					);
				};
			}
		}

		verify()
			.then((res) => {
				handleQueryResponse(res.data);
			})
			.catch(async (error) => {
				[996, 999].indexOf(error.code) === -1
					? setVerifying(true)
					: (() => {})();
				await handleSDKScanError(error);
			});
	}, []);

	useEffect(() => {
		return () => {
			if (!sdk) return;
			sdk.stopARCodeDetector();
		};
	}, []);

	if (verifying) return <Loading content={t('scan.verifying')} />;

	return (
		<MotionWrapper>
			{torchAvailability && (
				<LightbulbIcon
					id={'torch'}
					sx={{
						zIndex: 10000,
						position: 'absolute',
						top: '8px',
						right: '2%',
						width: '40px',
						height: '40px',
						color: 'white',
					}}
				/>
			)}
			<Box sx={{ overflow: 'hidden' }}>
				<Box
					id={'parentElement'}
					sx={{
						position: 'relative !important',
						backgroundColor: 'black',
						maxWidth: 'sm',
						bottom: '56px',
					}}
					hidden
				/>
			</Box>
			<Stack
				direction="column"
				spacing={0.5}
				alignItems="center"
				justifyContent="center"
				sx={{
					zIndex: 10000,
					position: 'absolute',
					bottom: '35px',
					left: '10%',
					width: '80%',
				}}
			>
				<Typography
					color="primary.main"
					align="justify"
					style={{
						whiteSpace: 'pre-line',
						color: 'white',
						fontSize: 'larger',
					}}
				>
					{t('app.user.text1')}
				</Typography>
				<Grid
					container
					direction="row"
					justifyContent="space-around"
					alignItems="center"
					sx={{ color: 'white' }}
				>
					<Grid item>
						<NavBtn to={'/GetHelp'}>
							<Typography
								sx={{ color: 'white', fontSize: 'medium' }}
							>
								{t('app.user.text2')}
							</Typography>
						</NavBtn>
					</Grid>
					<Grid>|</Grid>
					<Grid item>
						<NavBtn to={'/KnownIssues'}>
							<Typography
								sx={{ color: 'white', fontSize: 'medium' }}
							>
								{t('issue.KnownIssues')}
							</Typography>
						</NavBtn>
					</Grid>
					<Grid>|</Grid>
					<Grid item>
						<NavBtn to={'/FAQ'}>
							<Typography
								sx={{ color: 'white', fontSize: 'medium' }}
							>
								{t('issue.FAQ')}
							</Typography>
						</NavBtn>
					</Grid>
				</Grid>
			</Stack>
		</MotionWrapper>
	);
};

export default Scan;
